import { Avatar, HStack, Text, VStack } from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';

import { EntryComment as EntryCommentType } from '../types';

export interface EntryCommentProps {
  comment: EntryCommentType;
}

/**
 * Displays a single comment with author info, timestamp, and content.
 * Used within expanded journal entry cards.
 */
export const EntryComment: React.FC<EntryCommentProps> = ({ comment }) => {
  const authorName = comment.author.fullName;

  const commentDate = parseISO(comment.createdAt);
  const timestamp = format(commentDate, 'MMM d, yyyy h:mm a');

  // Create initials from name for avatar fallback
  const initials = authorName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <HStack align="flex-start" spacing={2} width="100%">
      <Avatar
        size="sm"
        name={authorName}
        src={comment.author.avatarUrl || undefined}
        bg="surface.light"
        color="text.default"
        getInitials={() => initials}
      />
      <VStack align="flex-start" spacing={0} flex={1}>
        <HStack spacing={2}>
          <Text fontSize="sm" fontWeight={600} color="text.default">
            {authorName}
          </Text>
          <Text fontSize="xs" color="text.muted">
            {timestamp}
          </Text>
        </HStack>
        <Text fontSize="sm" color="text.default" whiteSpace="pre-wrap">
          {comment.content}
        </Text>
      </VStack>
    </HStack>
  );
};

EntryComment.displayName = 'EntryComment';
